import { z } from 'zod';
import type { BillingType } from '../enums.js';

export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const;
export type AuState = (typeof AU_STATES)[number];

/** Australian time zones a site can be in. */
export const AU_TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Perth',
  'Australia/Hobart',
] as const;

export const BILLING_FREQUENCIES = ['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Annually'] as const;

const digits = (s: string) => s.replace(/\s+/g, '');

/** A choice that may be left empty: "" (an unset dropdown) or null both mean none. */
const optionalChoice = <T extends readonly [string, ...string[]]>(values: T) =>
  z.preprocess((v) => (v === '' ? null : v), z.enum(values).nullish()).transform((v) => v ?? null);

/** ABN: 11 digits with the ATO check (subtract 1 from the first digit, weighted sum divisible by 89). */
export function isValidAbn(value: string): boolean {
  const d = digits(value);
  if (!/^\d{11}$/.test(d)) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const sum = d.split('').reduce((acc, ch, i) => acc + (Number(ch) - (i === 0 ? 1 : 0)) * weights[i]!, 0);
  return sum % 89 === 0;
}

/** ACN: 9 digits with the ASIC check digit. */
export function isValidAcn(value: string): boolean {
  const d = digits(value);
  if (!/^\d{9}$/.test(d)) return false;
  const weights = [8, 7, 6, 5, 4, 3, 2, 1];
  const sum = d.slice(0, 8).split('').reduce((acc, ch, i) => acc + Number(ch) * weights[i]!, 0);
  return (10 - (sum % 10)) % 10 === Number(d[8]);
}

/** "51 824 753 556" for an ABN, "004 085 616" for an ACN. */
export function formatAbnAcn(value: string): string {
  const d = digits(value);
  if (d.length === 11) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
  if (d.length === 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  return value;
}

// Optional text: blank becomes null, so "" never ends up in the database.
const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters`)
    .nullish()
    .transform((v) => (v ? v : null));

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Enter a valid email address')
  .nullish()
  .transform((v) => (v ? v : null));

export const optionalPhone = z
  .string()
  .trim()
  .max(32)
  .refine((v) => v === '' || /^[+()\d\s-]{6,}$/.test(v), 'Enter a phone number, e.g. 0483 788 168')
  .nullish()
  .transform((v) => (v ? v : null));

export const postcode = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{4}$/.test(v), 'Postcodes have 4 digits')
  .nullish()
  .transform((v) => (v ? v : null));

const address = {
  addressStreet: optional(128),
  addressSuburb: optional(64),
  addressState: optionalChoice(AU_STATES),
  addressPostcode: postcode,
};

export const organisationBody = z.object({
  businessName: z.string().trim().min(1, 'Enter the business name').max(128),
  tradingAs: optional(128),
  accountNumber: optional(16),
  abnAcn: z
    .string()
    .trim()
    // 11 or 9 digits. The ATO check digit only warns (on screen): a mistyped number can still be saved and fixed later.
    .refine((v) => v === '' || /^(\d{11}|\d{9})$/.test(digits(v)), 'An ABN has 11 digits, an ACN 9')
    .nullish()
    .transform((v) => (v ? formatAbnAcn(v) : null)),
  addressLevel: optional(32),
  ...address,
  contactName: optional(128),
  contactEmail: optionalEmail,
  contactPhone: optionalPhone,
  accountsContactName: optional(128),
  accountsContactEmail: optionalEmail,
  accountsContactPhone: optionalPhone,
  billingFrequency: optionalChoice(BILLING_FREQUENCIES),
  accountOpenedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date')
    .nullish()
    .or(z.literal(''))
    .transform((v) => (v ? v : null)),
  timezone: z.enum(AU_TIMEZONES).default('Australia/Sydney'),
  notes: optional(2000),
  /** Admins: Powersensor home charging for this organisation's drivers. */
  powersensorEnabled: z.boolean().default(false),
  /** $/kWh drivers are paid back for charging at home. Needed when Powersensor is on. */
  powersensorRate: z.preprocess(
    (v) => (v === '' || v === undefined ? null : typeof v === 'string' ? v.trim().replace(/^\$\s*/, '') : v),
    z.coerce
      .number({ invalid_type_error: 'Enter a rate in dollars, e.g. 0.30' })
      .min(0, 'Must be 0 or more')
      .max(5, 'That looks too high: rates are in dollars per kWh')
      .nullable(),
  ),
});
export type OrganisationBody = z.input<typeof organisationBody>;

/** Six decimal places (about 10 cm): "-33.85704545351948" -> "-33.857045". Pasted values can be much longer. */
const sixPlaces = (v: string | null | undefined) => (v ? String(Number(Number(v).toFixed(6))) : null);

/** A place on the map, as text (decimal degrees, e.g. "-37.8136"). Blank: none. Sites and chargers. */
export const latitudeField = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^-?\d{1,3}(\.\d+)?$/.test(v) && Math.abs(Number(v)) <= 90), 'A latitude, e.g. -37.8136')
  .nullish()
  .transform(sixPlaces);
export const longitudeField = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^-?\d{1,3}(\.\d+)?$/.test(v) && Math.abs(Number(v)) <= 180), 'A longitude, e.g. 144.9631')
  .nullish()
  .transform(sixPlaces);

export const siteBody = z.object({
  name: z.string().trim().min(1, 'Enter a name, e.g. Richmond office').max(128),
  ...address,
  latitude: latitudeField,
  longitude: longitudeField,
  timezone: z.enum(AU_TIMEZONES).default('Australia/Sydney'),
  /** Admins: the time-of-use schedule (peak, off-peak…) for prices here. Null: one price all day. */
  rateScheduleId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid('Choose a schedule').nullable()).optional(),
  notes: optional(2000),
});
export type SiteBody = z.input<typeof siteBody>;

/** How "Billed to" reads on screen. */
export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  organisation: 'Bill the organisation',
  public: 'Public charging',
  unbilled: 'Not billed',
};

const STATE_NAMES: Record<string, AuState> = {
  'australian capital territory': 'ACT',
  'new south wales': 'NSW',
  'northern territory': 'NT',
  queensland: 'QLD',
  'south australia': 'SA',
  tasmania: 'TAS',
  victoria: 'VIC',
  'western australia': 'WA',
};

/** "vic", "Victoria" or "VIC" -> "VIC"; anything unrecognised -> null. For older records. */
export function normaliseState(value: string | null | undefined): AuState | null {
  if (!value) return null;
  const v = value.trim();
  const upper = v.toUpperCase() as AuState;
  return (AU_STATES as readonly string[]).includes(upper) ? upper : (STATE_NAMES[v.toLowerCase()] ?? null);
}
